import React from 'react';

import './styles.scss';
import classNames from 'classnames';

import playPause from 'utils/playPause';

import getUniqueId from 'utils/getUniqueId';
import isIpadDevice from 'utils/isIpadDevice';
import {WebAudioVoice} from 'utils/audioEngine';

import {DASHARRAY_LENGTH} from 'constants/index';

import 'actions';
import strings from 'utils/strings';

class AudioPlayer extends React.Component {
	constructor() {
		super();

		this.uniqueId = `audio${getUniqueId()}`;

		this.state = {
			isPlaying: false,
			duration: 0, // ms
			currentTime: 0,
			isDone: false,
			isStarted: false,
		};
		this.tick = this.tick.bind(this);
		this.handleStartPlayer = this.handleStartPlayer.bind(this);
		this.handlePause = this.handlePause.bind(this);
		this.handlePlay = this.handlePlay.bind(this);
		this.handleEnded = this.handleEnded.bind(this);
		this.handleDurationChange = this.handleDurationChange.bind(this);
		this.handleCanPlay = this.handleCanPlay.bind(this);
		this.handleAutoplay = this.handleAutoplay.bind(this);
		this.queueAutoplay = this.queueAutoplay.bind(this);
		this.autoplayAttempts = 0;
		this.userInteracted = false;
		this.isIpad = isIpadDevice();
		this.ipadRetryArmed = false;
		this.ipadRetryListener = null;
		this.audioRef = null;
	}

	getRefId() {
		return this.props.id || this.uniqueId;
	}

	attachAudioListeners(voice) {
		voice.addEventListener('durationchange', this.handleDurationChange);
		voice.addEventListener('loadedmetadata', this.handleDurationChange);
		voice.addEventListener('loadeddata', this.handleCanPlay);
		voice.addEventListener('canplay', this.handleCanPlay);
		voice.addEventListener('ended', this.handleEnded);
		voice.addEventListener('pause', this.handlePause);
		voice.addEventListener('play', this.handlePlay);
	}

	detachAudioListeners(voice) {
		if (!voice) {
			return;
		}

		voice.removeEventListener('durationchange', this.handleDurationChange);
		voice.removeEventListener('loadedmetadata', this.handleDurationChange);
		voice.removeEventListener('loadeddata', this.handleCanPlay);
		voice.removeEventListener('canplay', this.handleCanPlay);
		voice.removeEventListener('ended', this.handleEnded);
		voice.removeEventListener('pause', this.handlePause);
		voice.removeEventListener('play', this.handlePlay);
	}

	createAudioVoice(src) {
		const {refs} = this.props;
		const id = this.getRefId();

		if (this.audioRef) {
			this.detachAudioListeners(this.audioRef);
			this.audioRef.destroy();
		}

		this.audioRef = new WebAudioVoice(src);
		this.attachAudioListeners(this.audioRef);

		if (refs && id) {
			refs[id] = this.audioRef;
		}
	}

	componentDidMount() {
		this.hasCompleted = false;
		this.createAudioVoice(this.props.src);

		if (this.props.autoplay) {
			try {
				this.audioRef.load();
			} catch (_) {}
			this.queueAutoplay();
		}
	}

	componentWillUnmount() {
		cancelAnimationFrame(this.animationFrame);

		if (this.audioRef) {
			this.detachAudioListeners(this.audioRef);
			this.audioRef.destroy();
		}

		clearTimeout(this.autoplayTimer);
		this.disarmIpadAutoplayRetry();
	}

	handleDurationChange() {
		try {
			const duration = this.audioRef.duration;

			if (duration) {
				this.setState({
					currentTime: duration,
					duration,
				});
			}
		} catch (e) {
			console.log('Some problem', e);
		}
	}

	handleAutoplay() {
		if (
			!this.props.autoplay ||
			!this.props.src ||
			!this.audioRef ||
			this.props.disabled ||
			this.userInteracted ||
			this.autoplayAttempts >= 3 ||
			(!this.audioRef.paused && !this.audioRef.ended)
		) {
			return;
		}

		this.autoplayAttempts += 1;
		const playRequest = this.handleStartPlayer({autoplay: true});

		if (this.isIpad) {
			// The shared Web Audio context should already be unlocked (see
			// utils/audioEngine.js + primeAudioPlayback), so this should
			// rarely be needed — kept as a safety net in case the context
			// was never unlocked (e.g. an entry point that skips language
			// selection) or a given WebKit build still hangs the promise
			// instead of rejecting when playback is blocked.
			this.armIpadAutoplayRetry();

			if (playRequest && typeof playRequest.catch === 'function') {
				playRequest.catch(() => {
					this.armIpadAutoplayRetry();
				});
			}
		}
	}

	armIpadAutoplayRetry() {
		if (!this.isIpad || this.ipadRetryArmed) {
			return;
		}

		this.ipadRetryArmed = true;

		const retry = () => {
			this.disarmIpadAutoplayRetry();

			if (
				!this.audioRef ||
				!this.props.autoplay ||
				this.props.disabled ||
				this.userInteracted ||
				(!this.audioRef.paused && !this.audioRef.ended)
			) {
				return;
			}

			this.handleStartPlayer({autoplay: true});
		};

		this.ipadRetryListener = retry;
		document.addEventListener('touchend', retry, true);
		document.addEventListener('click', retry, true);
	}

	disarmIpadAutoplayRetry() {
		if (!this.ipadRetryListener) {
			return;
		}

		document.removeEventListener('touchend', this.ipadRetryListener, true);
		document.removeEventListener('click', this.ipadRetryListener, true);
		this.ipadRetryListener = null;
		this.ipadRetryArmed = false;
	}

	handleCanPlay() {
		this.handleAutoplay();
	}

	queueAutoplay() {
		this.handleAutoplay();
		clearTimeout(this.autoplayTimer);
		this.autoplayTimer = setTimeout(this.handleAutoplay, 250);
	}

	componentWillReceiveProps(nextProps) {
		const {src} = this.props;
		if (src && src !== nextProps.src && this.audioRef) {
			this.handlePause();
		}
	}

	componentDidUpdate(prevProps) {
		const {src} = this.props;
		if (src !== prevProps.src) {
			this.hasCompleted = false;
			this.autoplayAttempts = 0;
			this.userInteracted = false;
			this.createAudioVoice(src);
		}

		if (
			this.props.autoplay &&
			(!prevProps.autoplay || src !== prevProps.src)
		) {
			this.queueAutoplay();
		}
	}

	tick() {
		const audioRef = this.audioRef;
		const hasStoppedPlaying = audioRef.ended || audioRef.paused;
		if (hasStoppedPlaying) {
			this.setState({
				isDone: true,
			});
		} else {
			this.setState({
				currentTime: audioRef.currentTime,
			});
		}

		if (!this.state.isDone) {
			this.animationFrame = requestAnimationFrame(this.tick);
		}
	}

	completeCallback() {
		if (this.hasCompleted) {
			return;
		}

		this.hasCompleted = true;

		if (this.props.onComplete) {
			this.props.onComplete();
		}
	}

	handlePlay() {
		this.hasCompleted = false;
		this.disarmIpadAutoplayRetry();
		this.setState(
			{
				isDone: false,
				isPlaying: true,
			},
			() => {
				this.tick();
			}
		);

		if (typeof this.props.onStart === 'function') {
			this.props.onStart();
		}
	}

	handlePause() {
		this.setState({
			isPlaying: false,
		});

		const audioRef = this.audioRef;
		const isEnded = audioRef.ended;

		if (isEnded) {
			this.completeCallback();
		}

		cancelAnimationFrame(this.animationFrame);

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	}

	handleEnded() {
		this.handlePause();
	}

	handleStartPlayer(options = {}) {
		const {autoplay} = options;

		if (
			autoplay &&
			this.audioRef &&
			!this.audioRef.paused &&
			!this.audioRef.ended
		) {
			return;
		}

		const playRequest = playPause(
			this.audioRef,
			this.props.refs,
			this.props.onErrorPlay,
			{
				suppressBlockedError: autoplay,
			}
		);

		return playRequest;
	}

	render() {
		const {disabled, inline, noBorder, strokeColor, storkeColor, outlineColor} =
			this.props;

		const {currentTime, duration, isPlaying} = this.state;

		let progress = 0;
		if (isPlaying && duration) {
			const percentageDone = (currentTime * 100) / duration;
			progress =
				DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		const styleVars = {
			'--audio-stroke': (strokeColor || storkeColor),
			'--audio-outline': outlineColor,
		};

		return (
			<button
				className={classNames({
					'lit-circle': true,
					'lit-audio': true,
					'is-playing': isPlaying,
					inline: inline,
					noBorder: noBorder,
				})}
				disabled={disabled}
				onClick={() => {
					if (!this.audioRef) {
						return;
					}
					this.userInteracted = true;
					this.handleStartPlayer();
				}}
				title={disabled ? strings.audioMax : null}
				style={styleVars}
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					style={{enableBackground: 'new 0 0 90 90'}}
					version="1.1"
					viewBox="0 0 90 90"
					width="90"
					x="0px"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					y="0px"
				>
					<g transform="rotate(-84 45 45)">
						 <circle
						   className="lit-audio__outline"
						   cx="45"
						   cy="45"
						   fill="none"
						   r="37"
						   strokeLinecap="round"
						   strokeWidth="5"
						 />
						 <circle
						   className="lit-audio__progress"
						   cx="45"
						   cy="45"
						   fill="none"
						   r="37"
						   strokeLinecap="round"
						   strokeWidth="6"
						   style={{ strokeDashoffset: `${progress}` }}
						 />
					</g>
				</svg>
				<div className="lit-audio__icon" />
			</button>
		);
	}
}

export default AudioPlayer;
